<template>
  <div>
    <!-- Header -->
    <base-header class="pb-6 pb-1 pt-5 pt-md-8 bg-gradient-custom" type="custom">
      <b-row>
        <b-col xl="3" md="6">
          <base-button icon type="primary" @click="redirectToForm">
            <span class="btn-inner--icon"><i class="ni ni-fat-add"></i></span>
            <span class="btn-inner--text">Nuevo Usuario</span>
          </base-button>
        </b-col>
      </b-row>
    </base-header>

    <!-- Mostrar Tabla -->
    <b-container fluid class="mt--4">
      <ReusableTable
        :title="tableTitle"
        :data="dataUSER"
        :columns="tableColumns"
        :actions="tableActions"
        :per-page="itemsPerPage"
        :total="totalUser"
        :search-query="searchQuery"
        @fetch-data="listadoUser"
        @search="onSearch"
      >
        <!-- Slot personalizado para la columna ID_USER -->
        <template #column-ID_USER="{ row }">
          <span class="font-weight-bold">{{ row.ID_USER }}</span>
        </template>

        <template #column-ID_ORG="{ row }">
          <span>{{
            getOrganizationName(row.ID_ORG)
          }}</span>
        </template>

        <!-- Slots para los iconos de las acciones -->
        <template #icon-edit>
          <i class="fas fa-pencil-alt"></i>
        </template>
        <template #icon-delete>
          <i class="far fa-trash-alt"></i>
        </template>
      </ReusableTable>
    </b-container>

    <!-- Modal para botón de eliminar -->
    <b-modal v-model="showDeleteModal" title="Confirmar Eliminación" size="md">
      <div class="py-3 text-center">
        <i class="far fa-trash-alt me-5 ni-3x text-red"></i>
        <p v-if="userToDelete">
          ¿Estás seguro de eliminar este usuario:
          <strong>{{ userToDelete.NAME_USER }}</strong
          >?
        </p>
        <p v-else>¿Estás seguro de eliminar este usuario?</p>
      </div>
      <template v-slot:modal-footer>
        <b-button variant="danger" @click="confirmDelete">Eliminar</b-button>
        <b-button variant="secondary" @click="showDeleteModal = false">
          Cancelar
        </b-button>
      </template>
    </b-modal>

    <!-- Modal para token expirado -->
    <token-expired-modal :show="showTokenExpiredModal"> </token-expired-modal>
  </div>
</template>

<script>
import axios from "axios";
import ReusableTable from "./Tables/RegularTables/TablaReu.vue";
import Modal from "../components/Modal.vue";
import Notifications from "../components/NotificationPlugin/Notifications.vue";
import TokenExpiredModal from '../components/TokenExpiredModal.vue';

export default {
  components: { ReusableTable, Modal, Notifications, TokenExpiredModal },
  data() {
    return {
      dataUSER: [], // Datos de la tabla
      organizaciones: [], // Datos de la tabla org
      showDeleteModal: false, // Controla la visibilidad del modal de eliminación
      userToDelete: null, // Usuario seleccionado para eliminar
      isLoading: true, // Estado de carga
      showTokenExpiredModal: false, // Controla la visibilidad del modal de token expirado
      currentPage: 1, // Página actual
      itemsPerPage: 10, // Elementos por página
      totalUser: 0, // Total de usuarios
      searchQuery: "", // Consulta de búsqueda
      tableTitle: "Listado de Usuarios", // Título de la tabla
      tableColumns: [
        { prop: "ID_USER", label: "ID" },
        { prop: "NAME_USER", label: "Nombre" },
        { prop: "ID_ORG", label: "Nombre Organización", minWidth: "90", },
        { prop: "ROL", label: "Rol"},
      ], // Columnas de la tabla
      tableActions: [
        {
          name: "edit",
          label: "Editar",
          class: "btn text-black",
          onClick: (row) => this.$router.push(`/UserEdit/${row.ID_USER}`),
        },
        {
          name: "delete",
          label: "Eliminar",
          class: "btn text-red",
          onClick: (row) => this.openDeleteModal(row),
        },
      ], // Acciones de la tabla
    };
  },
  watch: {
    // Observar cambios en la página actual
    currentPage() {
      this.listadoUser();
    },
    // Observar cambios en la consulta de búsqueda
    searchQuery() {
      this.currentPage = 1; // Reiniciar a la primera página al buscar
      this.listadoUser();
    },
  },
  methods: {
    // Abrir el modal de eliminación
    openDeleteModal(row) {
      this.userToDelete = row;
      this.showDeleteModal = true;
    },
    // Confirmar la eliminación de un usuario
    async confirmDelete() {
      if (!this.userToDelete) return;

      try {
        const token = localStorage.getItem("token");
        const response = await axios.delete(
          `${process.env.VUE_APP_API_URL}/eliminarUSER/${this.userToDelete.ID_USER}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          this.$notifications.addNotification({
            type: "success",
            message: "Usuario eliminado correctamente",
          });
        } else {
          this.$notifications.addNotification({
            type: "danger",
            message: response.data.message || "Error al eliminar el usuario",
          });
        }

        if(this.dataUSER.length === 1 && this.currentPage > 1) {
          this.currentPage -= 1;
        }
        this.listadoUser();
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.handleTokenExpired();
        } else {
          console.error("Error al eliminar el usuario:", error);
          this.$notifications.addNotification({
            type: "danger",
            message: "Error al eliminar el usuario",
          });
        }
      } finally {
        this.showDeleteModal = false;
        this.userToDelete = null;
      }
    },

    // Obtener la lista de usuarios
    async listadoUser({ page, perPage, search } = {}) {
      this.isLoading = true;
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/obtenerDatosUser`,
          {
            params: {
              page: page || this.currentPage,
              perPage: perPage || this.itemsPerPage,
              search: search || this.searchQuery,
            },
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          this.dataUSER = response.data.data;
          this.totalUser = response.data.total;
          this.currentPage = page || this.currentPage; // Actualizar la página actual
        } else {
          this.$notifications.addNotification({
            type: "danger",
            message: "Error al obtener datos",
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.handleTokenExpired();
        } else {
          this.$notifications.addNotification({
            type: "danger",
            message: "Error al obtener datos",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },
    async obtenerOrganizaciones() {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/obtenerTodasLasOrgs`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          this.organizaciones = response.data.data;
        } else {
          this.$notifications.addNotification({
            type: "danger",
            message: "Error al obtener organizaciones",
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.handleTokenExpired();
        } else {
          this.$notifications.addNotification({
            type: "danger",
            message: "Error al obtener organizaciones",
          });
        }
      }
    },
    getOrganizationName(idOrg) {
      const organizacion = this.organizaciones.find(
        (org) => org.ID_ORG === idOrg
      );
      return organizacion
        ? organizacion.NAME_ORG
        : "Organización no encontrada";
    },
    // Manejar la búsqueda
    onSearch(searchQuery) {
      this.searchQuery = searchQuery;
      this.listadoUser();
    },
    // Redirigir al formulario de creación
    redirectToForm() {
      this.$router.push("/UserAdd");
    },
    // Manejar token expirado
    handleTokenExpired() {
      this.showTokenExpiredModal = true;
      setTimeout(() => {
        this.$router.push("/login");
      }, 3000);
    },
  },
  mounted() {
    // Cargar la lista de usuarios al montar el componente
    this.listadoUser();
    this.obtenerOrganizaciones();
  },
};
</script>